"use client";

import { useRef } from "react";
import { BUTTON_SMALL } from "@/components/tool-kit/styles";
import {
  CoordinatePlane,
  PLOT_HEIGHT,
  PLOT_WIDTH,
  PlanePoint,
} from "@/components/tools/math/coordinate-plane";
import { useToolNumber } from "@/components/tools/tool-ui";
import { downloadPng } from "@/lib/tool-kit/download";
import { equalizeAspect, padRange } from "@/lib/tool-kit/math/plot";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

export interface LabelledPoint {
  x: number;
  y: number;
  label: string;
  color: string;
}

/**
 * The segment between two points with labelled points on or beside it; the line is
 * extended with a dashed stroke through the extra points (an external division).
 */
export function SegmentPlot({
  from,
  to,
  extras,
  title,
  downloadLabel,
  fileName,
}: {
  from: LabelledPoint;
  to: LabelledPoint;
  extras: LabelledPoint[];
  title: string;
  downloadLabel: string;
  fileName: string;
}) {
  const fmt = useToolNumber();
  const ref = useRef<SVGSVGElement>(null);
  const all = [from, to, ...extras];
  const xs = [0, ...all.map((p) => p.x)];
  const ys = [0, ...all.map((p) => p.y)];
  const span = Math.max(
    Math.max(...xs) - Math.min(...xs),
    Math.max(...ys) - Math.min(...ys),
    1
  );
  const [xMin, xMax] = padRange(Math.min(...xs), Math.max(...xs), 0.2, span);
  const [yMin, yMax] = padRange(Math.min(...ys), Math.max(...ys), 0.2, span);
  const viewport = equalizeAspect(
    { xMin, xMax, yMin, yMax },
    PLOT_WIDTH,
    PLOT_HEIGHT
  );
  const beyond = extras.filter((p) => {
    const t =
      from.x === to.x
        ? (p.y - from.y) / (to.y - from.y || 1)
        : (p.x - from.x) / (to.x - from.x);
    return t < 0 || t > 1;
  });
  const ends = [from, to, ...beyond];
  const first = ends.reduce((a, b) =>
    key(a, from, to) <= key(b, from, to) ? a : b
  );
  const last = ends.reduce((a, b) =>
    key(a, from, to) >= key(b, from, to) ? a : b
  );
  return (
    <div className="space-y-3">
      <CoordinatePlane
        formatTick={(value, decimals) =>
          fmt(value, { maximumFractionDigits: decimals })
        }
        svgRef={ref}
        title={title}
        viewport={viewport}
      >
        {(scale) => (
          <>
            {beyond.length > 0 ? (
              <line
                stroke={CHART_COLORS[0]}
                strokeDasharray="6 4"
                strokeWidth={2}
                x1={scale.x(first.x)}
                x2={scale.x(last.x)}
                y1={scale.y(first.y)}
                y2={scale.y(last.y)}
              />
            ) : null}
            <line
              stroke={CHART_COLORS[0]}
              strokeWidth={3}
              x1={scale.x(from.x)}
              x2={scale.x(to.x)}
              y1={scale.y(from.y)}
              y2={scale.y(to.y)}
            />
            {all.map((p) => (
              <PlanePoint
                color={p.color}
                cx={scale.x(p.x)}
                cy={scale.y(p.y)}
                key={`${p.label}`}
                label={p.label}
              />
            ))}
          </>
        )}
      </CoordinatePlane>
      <button
        className={BUTTON_SMALL}
        onClick={() => {
          if (ref.current) {
            downloadPng(fileName, ref.current).catch(() => undefined);
          }
        }}
        type="button"
      >
        {downloadLabel}
      </button>
    </div>
  );
}

/** Position of a point along the from→to direction, for ordering collinear points. */
function key(p: LabelledPoint, from: LabelledPoint, to: LabelledPoint): number {
  return (p.x - from.x) * (to.x - from.x) + (p.y - from.y) * (to.y - from.y);
}
